import ProductTemplateColumnsDropdown from "components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown";
import { InputNumber } from "primereact/inputnumber";
import { CellType } from "utils/enums";
import {
  IProductTemplateDropdownChangeEvent,
  ProductTemplateDropdown,
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import { DropdownChangeEvent } from "primereact/dropdown";
import React, { useCallback } from "react";
import { T } from "services/lang/LanguageService";
import { DatasetScatterProps, ProductTemplateScatter, YAxisIdentifier } from "../interfaces";

interface DatasetScatterChartTemplateOptionsProps {
  config: DatasetScatterProps;
  handleChange: (
    field: keyof DatasetScatterProps,
    value: string | string[] | number | boolean | null | YAxisIdentifier[] | ProductTemplateScatter
  ) => void;
}

const DatasetScatterChartTemplateOptions = ({
  config,
  handleChange,
}: DatasetScatterChartTemplateOptionsProps) => {
  const handleProductEntryIdChange = useCallback((e: IProductTemplateDropdownChangeEvent) => {
    const updatedProductTemplate = {
      productTemplateRefName: e.refName,
      productTemplateId: e.value,
    };

    handleChange("productTemplate", updatedProductTemplate);
  }, []);

  const handleProductEntryColumnsChange = useCallback(
    (e: DropdownChangeEvent) => {
      const existingProductTemplate = config.productTemplate ?? {};

      const updatedProductTemplate = {
        ...existingProductTemplate,
        [e.target.id]: e.value ?? null,
      };

      handleChange("productTemplate", updatedProductTemplate);
    },
    [config.productTemplate]
  );

  const handleLastEntryCountChange = useCallback(
    (e: number) => {
      const existingProductTemplate = config.productTemplate ?? {};
      const updatedProductTemplate = {
        ...existingProductTemplate,
        lastEntryCount: e,
      };
      handleChange("productTemplate", updatedProductTemplate);
    },
    [config.productTemplate]
  );

  return (
    <div>
      <div className="flex flex-row justify-content-between gap-3">
        <div className="flex flex-column gap-3 w-6">
          <div className="flex flex-column gap-2 w-full">
            <label htmlFor="productTemplateId">{T("load-conf.product-template")}</label>
            <ProductTemplateDropdown
              value={Number(config.productTemplate?.productTemplateId?.toString())}
              onChange={handleProductEntryIdChange}
            />
          </div>
          <div className="flex flex-column gap-2 w-full">
            <label htmlFor="latestEntryCount">{T("label.latest-entry-count")}</label>
            <InputNumber
              value={config.productTemplate?.lastEntryCount ?? null}
              onChange={e => handleLastEntryCountChange(e.value ?? 0)}
            />
          </div>
        </div>
        <div className="flex flex-column gap-3 w-6">
          <div className="flex flex-column gap-2">
            <label htmlFor="templateXAttributeId">{T("action.select-x-attribute")}</label>
            <ProductTemplateColumnsDropdown
              id="templateXAttributeId"
              value={
                config.productTemplate?.templateXAttributeId
                  ? [config.productTemplate.templateXAttributeId.toString()]
                  : []
              }
              onChange={e => handleProductEntryColumnsChange(e)}
              selectedTemplate={{
                refName: config.productTemplate?.productTemplateRefName ?? "",
                value: parseInt((config.productTemplate?.productTemplateId ?? "").toString(), 10),
              }}
              showClear
              allowedTypes={[CellType.DOUBLE, CellType.LONG]}
              singleSelect
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="templateYAttributeId">{T("action.select-y-attribute")}</label>
            <ProductTemplateColumnsDropdown
              id="templateYAttributeId"
              value={
                config.productTemplate?.templateYAttributeId
                  ? [config.productTemplate.templateYAttributeId.toString()]
                  : []
              }
              onChange={e => handleProductEntryColumnsChange(e)}
              selectedTemplate={{
                refName: config.productTemplate?.productTemplateRefName ?? "",
                value: parseInt((config.productTemplate?.productTemplateId ?? "").toString(), 10),
              }}
              showClear
              allowedTypes={[CellType.DOUBLE, CellType.LONG]}
              singleSelect
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="templateSizeAttributeId">{T("action.select-size-attribute")}</label>
            <ProductTemplateColumnsDropdown
              id="templateSizeAttributeId"
              value={
                config.productTemplate?.templateSizeAttributeId
                  ? [config.productTemplate.templateSizeAttributeId.toString()]
                  : []
              }
              onChange={e => handleProductEntryColumnsChange(e)}
              selectedTemplate={{
                refName: config.productTemplate?.productTemplateRefName ?? "",
                value: parseInt((config.productTemplate?.productTemplateId ?? "").toString(), 10),
              }}
              showClear
              allowedTypes={[CellType.DOUBLE, CellType.LONG]}
              singleSelect
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="templateColorAttributeId">{T("action.select-color-attribute")}</label>
            <ProductTemplateColumnsDropdown
              id="templateColorAttributeId"
              value={
                config.productTemplate?.templateColorAttributeId
                  ? [config.productTemplate.templateColorAttributeId.toString()]
                  : []
              }
              onChange={e => handleProductEntryColumnsChange(e)}
              selectedTemplate={{
                refName: config.productTemplate?.productTemplateRefName ?? "",
                value: parseInt((config.productTemplate?.productTemplateId ?? "").toString(), 10),
              }}
              showClear
              allowedTypes={[CellType.DOUBLE, CellType.LONG]}
              singleSelect
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatasetScatterChartTemplateOptions;
